import { useMutation } from "@apollo/client/react";
import {
  DSButton,
  DSTextarea,
} from "@components/design-system/molecules";
import { DSModal } from "@components/design-system/organisms/DSModal";
import { faRotate } from "@fortawesome/pro-solid-svg-icons";
import FLAG_NOTE_TO_SUPPORT from "@graphql/mutations/FlagNoteToSupport";
import { RETRY_FAILED_AUTO_SOAP } from "@graphql/mutations/RetryFailedAutoSoap";
import { useCurrentUser } from "@hooks";
import {
  usePremiumFeature,
  UserPermissions,
} from "@hooks/use-premium-feature";
import mixpanel from "mixpanel-browser";
import { useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { alert } from "../../common/Alert";

export default function SomethingWrongModal({
  note,
  refetchNote,
  shouldShowFlagNoteModal,
  setShouldShowFlagNoteModal,
}) {
  const { control, getValues, reset } = useForm({
    defaultValues: {
      flagComment: "",
    },
  });
  const { hasFeatureAccess } = usePremiumFeature();
  const navigate = useNavigate();
  const { data: userData } = useCurrentUser();
  const [retryFailedAutoSoap] = useMutation(RETRY_FAILED_AUTO_SOAP);
  const [flagNoteToSupport] = useMutation(FLAG_NOTE_TO_SUPPORT);

  let submitting = useRef(false);

  function handleSubmit() {
    if (submitting.current) {
      return false;
    }
    submitting.current = true;
    const data = getValues();
    flagNoteToSupport({
      variables: {
        email: userData?.user?.email,
        customerName: `${userData?.user?.firstName} ${userData?.user?.lastName}`,
        comment: data?.flagComment,
        noteUuid: note?.uuid,
        title: note?.title,
      },
    })
      .then(() => {
        alert(
          "success",
          hasFeatureAccess(UserPermissions.HUMAN_SUPPORT_ENABLED)
            ? "Sent info to our support team. We'll reach out when it's fixed!"
            : "Thanks! Your feedback has been recorded.",
        );
        reset();
        setShouldShowFlagNoteModal(false);
      })
      .catch((err) => console.log(err))
      .finally(() => {
        submitting.current = false;
      });
  }

  const handleRetryFailedAutoSoap = async () => {
    try {
      await retryFailedAutoSoap({
        variables: {
          noteUuid: note?.uuid,
          jobType: note?.jobType,
          templateUuid: note?.autoTemplates?.uuid,
        },
      });
      refetchNote();
      alert("success", "Reprocessing this note!");
      navigate(`/dashboard`);
      setShouldShowFlagNoteModal(false);
      mixpanel.track("Note reprocessed", {
        noteUuid: note?.uuid,
        jobType: note?.jobType,
        templateUuid: note?.templateUuid,
        source: "Something Wrong Button",
      });
    } catch (e) {
      mixpanel.track("Failed to reprocess note", {
        noteUuid: note?.uuid,
        jobType: note?.jobType,
        templateUuid: note?.templateUuid,
        error: e.message,
        source: "Something Wrong Button",
      });
      if (e.message === "note.autoTemplates is null") {
        alert(
          "error",
          "The template for this note has been deleted. Please use the 'Use Template' button to apply a new template.",
        );
      } else {
        alert(
          "error",
          "Failed to reprocess the note. Please try again later.",
        );
      }
    }
  };

  return (
    <DSModal
      isOpen={shouldShowFlagNoteModal}
      onClose={() => setShouldShowFlagNoteModal(false)}
      title="Something Wrong?"
      actions={[
        {
          label: "Close",
          variant: "cancel",
          onClick: () => setShouldShowFlagNoteModal(false),
        },
      ]}
      dataTestId="something-wrong-modal"
    >
      <div className="flex flex-row items-start divide-x dark:divide-gray-600/50 p-2">
        <div className="flex-1 pr-6 space-y-2">
          <h4>Retry Processing</h4>
          <div className="flex flex-row items-center space-x-2 border border-gray-300 dark:border-gray-700 rounded-full justify-between">
            <h4 className="text-sm md:text-base mx-4 truncate">
              {note?.jobTypeName || note?.jobType}
            </h4>
            <DSButton
              onClick={handleRetryFailedAutoSoap}
              icon={faRotate}
              dataTestId="retry-processing-button"
            >
              Retry
            </DSButton>
          </div>

          <h5 className="text-xs md:text-sm">
            If you'd like to apply a different template, please close
            this menu and click the <strong>Use Template</strong>{" "}
            button instead.
          </h5>
        </div>
        <div className="flex flex-col flex-1 pl-6 space-y-2">
          <h4>Send to Support</h4>
          <h5 className="text-xs md:text-sm">
            {hasFeatureAccess(UserPermissions.HUMAN_SUPPORT_ENABLED)
              ? "Let us know what's wrong, and we'll look into it. More details are preferred!"
              : "Something wrong? Your feedback helps us improve Scribenote's AI for everyone. More details are preferred!"}
          </h5>
          <Controller
            render={({ field }) => (
              <DSTextarea
                minRows={5}
                placeholder="The note is blank!"
                dataTestId="flag-comment-textarea"
                {...field}
              />
            )}
            control={control}
            name="flagComment"
          />
          <div className="self-end">
            <DSButton
              onClick={() => handleSubmit()}
              dataTestId="send-feedback-button"
            >
              Send →
            </DSButton>
          </div>
        </div>
      </div>
    </DSModal>
  );
}
